import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/api/client";
import { Card, Badge, EmptyState } from "@/components/ui";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import { getSocket } from "@/hooks/useSocket";
import { useAuthStore } from "@/store/authStore";
import toast from "react-hot-toast";
import clsx from "clsx";

export default function Arena() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [mode, setMode] = useState(null); // null | "irl" | "remote"
  const [view, setView] = useState("select"); // select | lobby | game
  const [room, setRoom] = useState(null);
  const [joinCode, setJoinCode] = useState("");
  const [players, setPlayers] = useState([]);

  const { data: quizzesData } = useQuery({
    queryKey: ["quizzes"],
    queryFn: () => api.get("/quizzes").then((r) => r.data),
  });
  const quizzes = quizzesData?.quizzes || [];

  const createRoom = useMutation({
    mutationFn: (payload) => api.post("/multiplayer/rooms", payload),
    onSuccess: (res) => {
      setRoom(res.data.room);
      setPlayers([{ userId: user.id, name: user.name, avatar: user.avatar, score: 0 }]);
      setView("lobby");
      const socket = getSocket();
      socket?.emit("room:join", { roomCode: res.data.room.code });
    },
  });

  const joinRoom = useMutation({
    mutationFn: (code) => api.post(`/multiplayer/rooms/${code}/join`),
    onSuccess: (res) => {
      setRoom(res.data.room);
      setView("lobby");
      const socket = getSocket();
      socket?.emit("room:join", { roomCode: res.data.room.code });
      toast.success("Raum beigetreten! 🎮");
    },
    onError: (err) => toast.error(err.response?.data?.error || "Raum nicht gefunden"),
  });

  if (view === "select" || !mode) {
    return (
      <div className="space-y-5 animate-fade-in">
        {/* Header */}
        <div className="text-center py-3">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-3"
            style={{ background: "linear-gradient(135deg, rgba(139,92,246,0.2), rgba(6,182,212,0.1))", border: "1px solid rgba(139,92,246,0.3)" }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" strokeWidth="1.5" stroke="#a78bfa" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
            </svg>
          </div>
          <h1 className="text-2xl font-black text-white">Arena</h1>
          <p className="text-gray-500 text-sm mt-1">Wähle deinen Spielmodus</p>
        </div>

        {/* METEOR STORM – Custom Minigame featured card */}
        <button
          onClick={() => navigate("/minigame")}
          className="w-full relative overflow-hidden rounded-3xl p-5 text-left transition-all duration-300 group"
          style={{
            background: "linear-gradient(135deg, #3b1764 0%, #1e1b4b 50%, #0c0c1e 100%)",
            border: "1px solid rgba(139,92,246,0.4)",
            boxShadow: "0 8px 40px rgba(139,92,246,0.2)",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 16px 60px rgba(139,92,246,0.35)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 8px 40px rgba(139,92,246,0.2)"; }}
        >
          {/* Background stars */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-40" xmlns="http://www.w3.org/2000/svg">
            {Array.from({length:20},(_,i)=>(
              <circle key={i} cx={`${5+i*4.8}%`} cy={`${10+Math.sin(i)*40}%`} r={Math.random()*1.2+0.3} fill="white" opacity={Math.random()*0.6+0.2}/>
            ))}
          </svg>
          {/* Glow orb */}
          <div className="absolute -top-8 -right-8 w-36 h-36 rounded-full opacity-30 group-hover:opacity-50 transition-opacity duration-300"
            style={{ background: "radial-gradient(circle, #8b5cf6, transparent)" }}/>

          <div className="relative flex items-start gap-4">
            {/* Icon */}
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{ background: "rgba(139,92,246,0.25)", border: "1px solid rgba(139,92,246,0.4)" }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" strokeWidth="1.5" stroke="#c4b5fd" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest"
                  style={{ background: "rgba(251,191,36,0.2)", color: "#fbbf24", border: "1px solid rgba(251,191,36,0.3)" }}>
                  Exklusiv
                </span>
                <span className="text-[9px] font-black text-violet-400 uppercase tracking-widest">Custom Minigame</span>
              </div>
              <h3 className="text-xl font-black text-white mb-1">
                Meteor<span style={{ color: "#a78bfa" }}>Storm</span>
              </h3>
              <p className="text-xs text-gray-400 leading-relaxed">
                Schieß Lern-Meteore ab, sammle Punkte und verdiene echte Coins & XP. Je besser du spielst, desto mehr verdienst du!
              </p>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {["Mathe","Deutsch","Natur","Logik"].map((c) => (
                  <span key={c} className="text-[9px] font-bold px-2 py-0.5 rounded-full"
                    style={{ background: "rgba(255,255,255,0.06)", color: "#9ca3af", border: "1px solid rgba(255,255,255,0.08)" }}>
                    {c}
                  </span>
                ))}
              </div>
            </div>
            <svg className="flex-shrink-0 mt-1 group-hover:translate-x-1 transition-transform duration-200" width="18" height="18" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="#8b5cf6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </div>
        </button>

        {/* Multiplayer modes */}
        <div className="grid grid-cols-2 gap-3">
          {/* IRL Mode */}
          <button
            onClick={() => { setMode("irl"); setView("lobby-new"); }}
            className="relative group overflow-hidden flex flex-col gap-3 p-4 rounded-2xl border text-left transition-all duration-300"
            style={{ background: "rgba(6,182,212,0.05)", borderColor: "rgba(6,182,212,0.2)" }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(6,182,212,0.1)"; e.currentTarget.style.borderColor = "rgba(6,182,212,0.4)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(6,182,212,0.05)"; e.currentTarget.style.borderColor = "rgba(6,182,212,0.2)"; e.currentTarget.style.transform = "none"; }}
          >
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "rgba(6,182,212,0.15)", color: "#06b6d4" }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            </div>
            <div>
              <p className="font-black text-sm text-white">Lokal</p>
              <p className="text-[10px] text-gray-500 mt-0.5">1 Gerät, alle dabei</p>
            </div>
          </button>

          {/* Remote Mode */}
          <button
            onClick={() => { setMode("remote"); setView("lobby-new"); }}
            className="relative group overflow-hidden flex flex-col gap-3 p-4 rounded-2xl border text-left transition-all duration-300"
            style={{ background: "rgba(139,92,246,0.05)", borderColor: "rgba(139,92,246,0.2)" }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(139,92,246,0.1)"; e.currentTarget.style.borderColor = "rgba(139,92,246,0.4)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(139,92,246,0.05)"; e.currentTarget.style.borderColor = "rgba(139,92,246,0.2)"; e.currentTarget.style.transform = "none"; }}
          >
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "rgba(139,92,246,0.15)", color: "#8b5cf6" }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
              </svg>
            </div>
            <div>
              <p className="font-black text-sm text-white">Online</p>
              <p className="text-[10px] text-gray-500 mt-0.5">Echtzeit, überall</p>
            </div>
          </button>
        </div>

        {/* Join existing room */}
        <Card className="p-4">
          <p className="text-xs font-black text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="#6b7280" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            Raum beitreten
          </p>
          <div className="flex gap-2">
            <Input
              value={joinCode}
              onChange={setJoinCode}
              placeholder="Raum-Code…"
              className="flex-1"
            />
            <Button
              onClick={() => joinRoom.mutate(joinCode)}
              loading={joinRoom.isPending}
              disabled={!joinCode.trim()}
            >
              Join
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (view === "lobby-new") {
    return (
      <div className="space-y-5 animate-fade-in">
        <button onClick={() => { setMode(null); setView("select"); }} className="text-gray-400 hover:text-white transition-colors text-sm font-bold">← Zurück</button>

        <div className="text-center">
          <div className="text-4xl mb-2">{mode === "irl" ? "🤝" : "🌐"}</div>
          <h2 className="text-xl font-black">{mode === "irl" ? "Lokale Runde" : "Online Runde"}</h2>
          <p className="text-gray-400 text-sm mt-1">Quiz auswählen und starten</p>
        </div>

        <Card className="p-5">
          <h3 className="font-black text-sm mb-3">📚 Quiz wählen</h3>
          <div className="space-y-2 max-h-64 overflow-y-auto scrollbar-hide">
            {quizzes.slice(0, 8).map((q) => (
              <button
                key={q.id}
                onClick={() => createRoom.mutate({ mode, quizId: q.id })}
                className="w-full flex items-center gap-3 p-3 bg-nexus-s2 border border-nexus-border rounded-xl hover:bg-nexus-s3 transition-colors text-left"
              >
                <span className="text-xl">{q.emoji}</span>
                <div>
                  <p className="font-bold text-sm">{q.title}</p>
                  <p className="text-xs text-gray-400">{q.questions?.length} Fragen</p>
                </div>
              </button>
            ))}
          </div>
        </Card>
      </div>
    );
  }

  if (view === "lobby" && room) {
    return (
      <div className="space-y-5 animate-fade-in">
        <Card className="p-6 text-center" glow>
          <div className="text-4xl mb-3">{mode === "irl" ? "🤝" : "🌐"}</div>
          <h2 className="text-xl font-black mb-1">{mode === "irl" ? "Lokale Lobby" : "Online Lobby"}</h2>
          <p className="text-gray-400 text-sm mb-4">Warte auf Mitspieler...</p>

          <div className="inline-block bg-nexus-s2 border border-violet-500/40 rounded-2xl px-6 py-3 mb-4">
            <p className="text-xs text-gray-400 font-bold mb-1">RAUM-CODE</p>
            <p className="text-3xl font-black text-gradient-flow tracking-widest">{room.code}</p>
          </div>

          <p className="text-xs text-gray-500">
            {mode === "irl"
              ? "Gebt alle denselben Code auf euren Geräten ein"
              : "Sendet den Code an eure Freunde"}
          </p>
        </Card>

        {/* Players */}
        <Card className="p-4">
          <h3 className="font-black text-sm mb-3">👥 Spieler ({players.length})</h3>
          <div className="space-y-2">
            {players.map((p) => (
              <div key={p.userId} className="flex items-center gap-3 p-2.5 bg-nexus-s2 rounded-xl border border-nexus-border">
                <span className="text-xl">{p.avatar || "🎮"}</span>
                <span className="font-bold text-sm flex-1">{p.name}</span>
                {p.userId === user.id && <Badge color="gold">Host</Badge>}
                <Badge color="green">Online</Badge>
              </div>
            ))}
            <div className="flex items-center gap-3 p-2.5 bg-nexus-s2/40 rounded-xl border border-dashed border-nexus-border opacity-40">
              <span className="text-xl">❓</span>
              <span className="text-sm text-gray-500">Wartet auf Spieler...</span>
            </div>
          </div>
        </Card>

        <Button
          full
          size="lg"
          onClick={() => {
            toast.success("Spiel gestartet! 🎮");
            getSocket()?.emit("room:start", { roomId: room.id });
            setView("game");
          }}
        >
          🚀 Spiel starten!
        </Button>
        <Button variant="secondary" full onClick={() => { setMode(null); setView("select"); setRoom(null); }}>Abbrechen</Button>
      </div>
    );
  }

  // Simple game view (placeholder — in production: full quiz engine)
  return (
    <div className="space-y-4 animate-fade-in">
      <Card className="p-8 text-center">
        <div className="text-5xl mb-4 animate-float">🎮</div>
        <h2 className="text-2xl font-black mb-2">Spiel läuft!</h2>
        <p className="text-gray-400 text-sm mb-6">
          {mode === "irl" ? "Pass das Gerät zum nächsten Spieler!" : "Alle spielen gleichzeitig auf ihren Geräten!"}
        </p>
        <Button onClick={() => { setMode(null); setView("select"); setRoom(null); }} variant="secondary">
          ← Zurück zur Übersicht
        </Button>
      </Card>
    </div>
  );
}
